/* eslint react-hooks/exhaustive-deps: "warn" */

import { type ReactNode, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './RegisGovernance.module.scss';

type Translate = (key: string) => string;

type QaRow = {
  n: string;
  q: string;
  a: ReactNode;
  aText: string;
};

const getQaRows = (t: Translate): QaRow[] => [
  {
    n: '01',
    q: t('Where does the answer come from?'),
    a: (
      <>
        {t('Primary government registry records, retrieved when the request runs.')}{' '}
        <b>{t('Not model recall, and not a score derived from someone else’s dataset.')}</b>
      </>
    ),
    aText: t(
      "Primary government registry records, retrieved when the request runs. Not model recall, and not a score derived from someone else's dataset.",
    ),
  },
  {
    n: '02',
    q: t('Can we reproduce it months later?'),
    a: (
      <>
        <b>{t('Yes.')}</b>{' '}
        {t(
          'Every value returns with its register, filing reference and retrieval timestamp, so the answer can be rebuilt against the original filing long after it was generated.',
        )}
      </>
    ),
    aText: t(
      'Yes. Every value returns with its register, filing reference and retrieval timestamp, so the answer can be rebuilt against the original filing long after it was generated.',
    ),
  },
  {
    n: '03',
    q: t('What happens when it does not know?'),
    a: (
      <>
        {t('It returns null and names the register that does not publish the field.')}{' '}
        <b>{t('No estimate, modelled figure or comparable-company value is substituted for a missing filing.')}</b>
      </>
    ),
    aText: t(
      'It returns null and names the register that does not publish the field. No estimate, modelled figure or comparable-company value is substituted for a missing filing.',
    ),
  },
  {
    n: '04',
    q: t('Is our query data used for training?'),
    a: (
      <>
        <b>{t('No.')}</b>{' '}
        {t(
          'Questions and payloads sent to the API are excluded from model training, with the controls set out in the data processing terms.',
        )}
      </>
    ),
    aText: t(
      'No. Questions and payloads sent to the API are excluded from model training, with the controls set out in the data processing terms.',
    ),
  },
  {
    n: '05',
    q: t('What certifications do you hold?'),
    a: (
      <>
        <b>ISO 27001</b>
        {t(
          ', and registration with the UK Information Commissioner’s Office. Security documentation and the data processing agreement are available under NDA before contract.',
        )}
      </>
    ),
    aText: t(
      "ISO 27001, and registration with the UK Information Commissioner's Office. Security documentation and the data processing agreement are available under NDA before contract.",
    ),
  },
  {
    n: '06',
    q: t('Can we redistribute the output?'),
    a: (
      <>
        {t(
          'Under a redistribution licence. Internal use, customer-facing display and redistribution are licensed separately,',
        )}{' '}
        <b>{t('so contract scope is explicit rather than assumed.')}</b>
      </>
    ),
    aText: t(
      'Under a redistribution licence. Internal use, customer-facing display and redistribution are licensed separately, so contract scope is explicit rather than assumed.',
    ),
  },
];

export const RegisGovernance = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);
  const visible = useIntersection(rootRef, { threshold: 0.1 });
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const qaRows = getQaRows(t);

  const handleCopy = (row: QaRow, index: number) => {
    navigator.clipboard
      .writeText(`Q: ${row.q}\nA: ${row.aText}`)
      .then(() => {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex((current) => (current === index ? null : current)), 1600);
      })
      .catch(() => undefined);
  };

  return (
    <Section className={styles.root}>
      <div ref={rootRef} className={cn(styles.content, visible && styles.contentIn)}>
        <div className={styles.head}>
          <TitleBlock
            title={t('The questions your second line will ask.')}
            description={t(
              'Every one of these arrives before a data source is approved. The answers are here so you can send them on without waiting for us.',
            )}
            align="left"
          />
        </div>

        <div className={styles.qa}>
          <div className={styles.qaHead}>
            <span className={styles.qaTitle}>{t('Vendor assessment')}</span>
            <span className={styles.qaMeta}>Regis API &middot; {t('six standard questions')}</span>
          </div>

          {qaRows.map((row, index) => (
            <div key={row.n} className={styles.qaRow}>
              <span className={styles.qaIndex}>{row.n}</span>

              <div className={styles.qaBody}>
                <h3 className={styles.qaQuestion}>{row.q}</h3>
                <p className={styles.qaAnswer}>{row.a}</p>
              </div>

              <button
                type="button"
                className={cn(styles.qaCopy, copiedIndex === index && styles.qaCopyDone)}
                onClick={() => handleCopy(row, index)}
              >
                {copiedIndex === index ? t('Copied') : t('Copy')}
              </button>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
